<template>
  <section class="py-space-lg py-md-space-xxl">
    <div class="container">
      <div class="row g-0">
        <div class="col-12">
          <div
            class="header-image"
            :style="bgImage"
            :class="{ 'sca-lazy': bgImageLazy }"
            :data-background-image="dataBgImage"></div>
        </div>
      </div>

      <div class="row g-0 pt-space-sm pt-md-space-xl px-lg-space-xxl">
        <div class="col-12 col-md-6 col-lg-7">
          <div class="mb-space-md me-md-space-md">
            <component :is="titleLevel" class="mb-space-sm" :class="titleClass">
              {{ title }}
            </component>
            <slot name="body" />
          </div>
        </div>
        <div class="col-12 col-md-6 col-lg-5">
          <div class="mx-md-space-md">
            <card-icon
              :iconSource="iconSource"
              :iconAlt="iconAlt"
              :displayCta="false"
              :displayTitle="false"
              :cardBackgroundVariant="cardBgVariant"
              :borderVariant="cardBorderVariant"
              :highlightLeft="highlightLeft">
              <div :class="cardTextClass" v-html="cardText"></div>
            </card-icon>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import CardIcon from "@rds-vue-ui/card-icon/CardIcon.vue";
import lozad from "lozad";
import { computed, onMounted, type StyleValue } from "vue";

interface Props {
  cardBorderVariant?: string;
  highlightLeft?: boolean;
  iconSource: string;
  iconAlt?: string;
  cardText?: string;
  cardTextVariant?: string;
  bgImageLazy?: boolean;
  cardBgVariant?: string;
  bgImageSource: string;
  title: string;
  titleLevel?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  titleSize?: "small" | "medium" | "large" | "xl";
  titleVariant?: string;
}

const props = withDefaults(defineProps<Props>(), {
  cardBorderVariant: "light-2",
  highlightLeft: true,
  iconAlt: "Alt Text",
  cardText: "Lorem Ipsum",
  cardTextVariant: "white",
  bgImageLazy: true,
  cardBgVariant: "dark-3",
  titleLevel: "h2",
  titleSize: "medium",
  titleVariant: "white",
});

const cardTextClass = computed(() => {
  return `text-${props.cardTextVariant}`;
});

const titleClass = computed(() => {
  return `${props.titleLevel}-${props.titleSize} text-${props.titleVariant}`;
});

const bgImage = computed(() => {
  const style: StyleValue = {};
  if (!props.bgImageLazy) {
    style["background-image"] = `url('${props.bgImageSource}')`;
  }
  return style;
});

const dataBgImage = computed(() => {
  return props.bgImageLazy ? props.bgImageSource : null;
});

onMounted(() => {
  if (typeof window !== "undefined" && window !== null && props.bgImageLazy) {
    const observer = lozad(".sca-lazy");
    observer.observe();
  }
});
</script>

<style lang="scss" scoped>
.card-class {
  min-height: 335px;
  max-width: 365px;
}
.header-image {
  height: 171px;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center top;
}
:deep(img.card-icon) {
  width: 66px;
  height: 54px;
}

/* Medium devices (tablets, 768px and up) */
@media (min-width: 768px) {
  .header-image {
    height: 380px;
  }
}

/* Large devices (desktops, 992px and up) */
@media (min-width: 992px) {
  .header-image {
    height: 550px;
  }
  :deep(img.card-icon) {
    width: 62px;
    height: 47px;
  }
}

@media (min-width: 1200px) {
  .header-image {
    height: 625px;
  }
}
</style>
